import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import * as candidateLinksAPI from '../../utils/api/candidateLinks.js';
import { getErrorMessage } from '../../utils/api/errors.js';

const isErrorLike = (value) =>
  value instanceof Error || (value && typeof value === 'object' && typeof value.message === 'string' && value.name);

export const fetchCandidateLinks = createAsyncThunk(
  'candidateLinks/fetchCandidateLinks',
  async (_, { rejectWithValue }) => {
    try {
      return await candidateLinksAPI.getCandidateLinks();
    } catch (error) {
      return rejectWithValue(getErrorMessage(error, 'Failed to fetch candidate links'));
    }
  }
);

export const createCandidateLink = createAsyncThunk(
  'candidateLinks/createCandidateLink',
  async (payload, { rejectWithValue }) => {
    try {
      return await candidateLinksAPI.createCandidateLink(payload);
    } catch (error) {
      return rejectWithValue(getErrorMessage(error, 'Failed to create candidate link'));
    }
  }
);

export const changeCandidateLinkStatus = createAsyncThunk(
  'candidateLinks/changeCandidateLinkStatus',
  async ({ id, status }, { rejectWithValue }) => {
    try {
      return await candidateLinksAPI.updateCandidateLinkStatus(id, status);
    } catch (error) {
      return rejectWithValue(error?.message || 'Failed to update link status');
    }
  }
);

export const removeCandidateLink = createAsyncThunk(
  'candidateLinks/removeCandidateLink',
  async (id, { rejectWithValue }) => {
    try {
      await candidateLinksAPI.deleteCandidateLink(id);
      return id;
    } catch (error) {
      return rejectWithValue(getErrorMessage(error, 'Failed to delete link'));
    }
  }
);

const initialState = {
  links: [],
  loading: false,
  error: null
};

const candidateLinksSlice = createSlice({
  name: 'candidateLinks',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchCandidateLinks.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchCandidateLinks.fulfilled, (state, action) => {
        state.loading = false;
        if (isErrorLike(action.payload)) {
          state.error = getErrorMessage(action.payload, 'Failed to fetch candidate links');
          state.links = [];
          return;
        }
        state.links = Array.isArray(action.payload) ? action.payload : [];
      })
      .addCase(fetchCandidateLinks.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(createCandidateLink.fulfilled, (state, action) => {
        state.links.unshift(action.payload);
      })
      .addCase(createCandidateLink.rejected, (state, action) => {
        state.error = action.payload;
      })
      .addCase(changeCandidateLinkStatus.fulfilled, (state, action) => {
        const updated = action.payload;
        if (!updated || updated.id == null || typeof updated.status !== 'string') return;
        const idx = state.links.findIndex((l) => String(l.id) === String(updated.id));
        if (idx !== -1) {
          state.links[idx] = updated;
        }
      })
      .addCase(changeCandidateLinkStatus.pending, (state) => {
        state.error = null;
      })
      .addCase(changeCandidateLinkStatus.rejected, (state, action) => {
        state.error = action.payload;
      })
      .addCase(removeCandidateLink.fulfilled, (state, action) => {
        state.links = state.links.filter((l) => l.id !== action.payload);
      })
      .addCase(removeCandidateLink.rejected, (state, action) => {
        state.error = action.payload;
      });
  }
});

export const { clearError } = candidateLinksSlice.actions;
export default candidateLinksSlice.reducer;
